import React, { useState } from 'react';
import { MODULES, MAPPED_CONTACTS } from '../../constants';
import { useApp } from '../../context/AppContext';

type TeamTab = 'teams' | 'members' | 'modules';

const TEAMS = [
  { name: 'North India Sales', members: 4, playbooks: 12, leads: 28, color: '#3B82F6', manager: 'Rahul Nair' },
  { name: 'South India Sales', members: 3, playbooks: 8,  leads: 14, color: '#8B5CF6', manager: 'Sneha Kapoor' },
  { name: 'Enterprise Team',   members: 2, playbooks: 5,  leads: 9,  color: '#22C55E', manager: 'Arjun Mehta' },
];

export default function TeamScreen() {
  const { toast } = useApp();
  const [tab, setTab] = useState<TeamTab>('teams');
  const modulesList = Object.values(MODULES);
  const coreModules = modulesList.filter(m => m.core);
  const optionalModules = modulesList.filter(m => !m.core);
  const [moduleStates, setModuleStates] = useState<Record<string, boolean>>(
    Object.fromEntries(modulesList.map(m => [m.id, m.enabled]))
  );

  const toggleModule = (id: string) => {
    const m = MODULES[id];
    if (m.core) { toast('Core modules cannot be disabled'); return; }
    setModuleStates(prev => ({ ...prev, [id]: !prev[id] }));
    toast(`${m.label} ${moduleStates[id] ? 'disabled' : 'enabled'}`);
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none',
    background: active ? 'white' : 'none',
    fontFamily: 'inherit', fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    color: active ? 'var(--navy)' : 'var(--text2)',
    boxShadow: active ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 24px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'white' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '11px', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="ti ti-users" style={{ fontSize: '20px', color: '#4F46E5' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--navy)' }}>Team & Module Management</div>
          <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '1px' }}>Manage teams, assign modules, and control visibility for reps and clients</div>
        </div>
        {tab === 'teams' && (
          <button onClick={() => toast('New team modal coming soon!')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '9px', background: '#C8973A', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
            <i className="ti ti-plus" /> New Team
          </button>
        )}
        {tab === 'members' && (
          <button onClick={() => toast('Add member modal coming soon!')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '9px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
            <i className="ti ti-user-plus" /> Add Member
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', padding: '10px 24px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {([
          { id: 'teams',   icon: 'ti-building',  label: 'Teams & Companies' },
          { id: 'members', icon: 'ti-user-check', label: 'Members'          },
          { id: 'modules', icon: 'ti-plug',        label: 'Module Config'    },
        ] as const).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={tabBtnStyle(tab === t.id)}>
            <i className={`ti ${t.icon}`} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── TEAMS PANEL ── */}
      {tab === 'teams' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: '16px', maxWidth: '1400px' }}>
            {TEAMS.map((team, i) => (
              <div key={i} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
                <div style={{ height: '6px', background: team.color }} />
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)', marginBottom: '3px' }}>{team.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text3)' }}>Manager: {team.manager}</div>
                    </div>
                    <button onClick={() => toast('Edit team coming soon!')} style={{ padding: '5px 10px', borderRadius: '6px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer', color: 'var(--text2)' }}>Edit</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '14px' }}>
                    {[{ val: team.members, label: 'Members' }, { val: team.playbooks, label: 'Playbooks' }, { val: team.leads, label: 'Leads' }].map(s => (
                      <div key={s.label} style={{ textAlign: 'center', padding: '10px', background: 'var(--surface)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--navy)' }}>{s.val}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => toast('Viewing members…')} style={{ flex: 1, padding: '7px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer' }}>View Members</button>
                    <button onClick={() => toast('Module settings opened!')} style={{ flex: 1, padding: '7px', borderRadius: '7px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Modules</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MEMBERS PANEL ── */}
      {tab === 'members' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <div style={{ maxWidth: '1000px' }}>
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                    {['Name', 'Role', 'Organization', 'Email', 'Phone', 'Segment', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MAPPED_CONTACTS.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: c.segment === 'client' ? 'var(--navy)' : c.segment === 'agent' ? '#6366F1' : '#22C55E', color: 'white', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{c.ini}</div>
                          <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{c.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', color: 'var(--text2)' }}>{c.role}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text3)' }}>{c.org}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text3)', fontSize: '12px' }}>{c.email}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text3)', fontSize: '12px' }}>{c.phone}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: c.segment === 'client' ? '#EBF8FF' : c.segment === 'agent' ? '#EEF2FF' : '#F0FFF4', color: c.segment === 'client' ? '#1E4E8C' : c.segment === 'agent' ? '#4F46E5' : '#276749', textTransform: 'capitalize' }}>
                          {c.segment}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button onClick={() => toast(`Editing ${c.name}`)} style={{ padding: '4px 9px', borderRadius: '5px', border: '1px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer', color: 'var(--text2)' }}>Edit</button>
                          <button onClick={() => toast(`Removed ${c.name}`)} style={{ padding: '4px 9px', borderRadius: '5px', border: '1px solid #FECDD3', background: '#FFF5F5', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer', color: '#C53030' }}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── MODULES PANEL ── */}
      {tab === 'modules' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <div style={{ maxWidth: '900px' }}>
            {/* Info banner */}
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <i className="ti ti-info-circle" style={{ fontSize: '16px', color: '#B45309', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#92400E', lineHeight: 1.6 }}>
                <strong>How it works:</strong> Core modules are always enabled. Optional modules can be toggled globally here, then assigned to specific teams. You can also hide modules from individual reps or clients.
              </div>
            </div>

            {/* Core modules */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '12px' }}>Core Modules (Always On)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {coreModules.map(m => (
                  <div key={m.id} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', opacity: .7 }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: m.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`ti ${m.icon}`} style={{ color: m.color, fontSize: '16px' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)' }}>{m.label}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{m.desc}</div>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: '#F0FFF4', color: '#276749', border: '1px solid #9AE6B4', flexShrink: 0 }}>Core</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Optional modules */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '12px' }}>Optional Modules</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {optionalModules.map(m => {
                  const on = moduleStates[m.id];
                  return (
                    <div key={m.id} style={{ background: 'white', border: `1.5px solid ${on ? m.color + '40' : 'var(--border)'}`, borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: on ? m.color + '20' : 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                        <i className={`ti ${m.icon}`} style={{ color: on ? m.color : 'var(--text3)', fontSize: '16px' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: on ? 'var(--navy)' : 'var(--text3)' }}>{m.label}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text3)', lineHeight: 1.5 }}>{m.desc}</div>
                      </div>
                      {/* Toggle */}
                      <div onClick={() => toggleModule(m.id)} style={{ cursor: 'pointer', flexShrink: 0, marginTop: '2px' }}>
                        <div style={{ width: '36px', height: '20px', borderRadius: '10px', background: on ? m.color : '#CBD5E0', position: 'relative', transition: 'background .2s' }}>
                          <div style={{ position: 'absolute', top: '2px', left: on ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .2s' }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
